'use client';

// ============================================================
// scan-stages : briques partagées du scanner (caméra + cadrage)
// ============================================================
// Ces deux étapes étaient enfermées dans scan-fee-screen.tsx. Le parcours
// « Envoyer un document » (expense-scan-screen.tsx) a besoin EXACTEMENT de la
// même caméra pleine résolution et du même cadrage à 4 poignées : on les
// sort ici plutôt que de les dupliquer, pour qu'une amélioration profite
// aux deux (portail salarié, inbox back-office, duplicata de caisse).
//
// Rien n'est dégradé au passage :
//  - la capture reste ImageCapture.takePhoto() en pleine résolution capteur,
//  - le détourage reste le modèle ML (U²-Net) + redressement OpenCV,
//  - le réglage manuel des coins et la loupe sont conservés.
//
// Ajouts de juillet 2026 (maquette frais v3) :
//  - bouton « Pivoter » sur le cadrage (photo prise de travers),
//  - `initialQuad` : rouvrir le cadrage D'UNE page déjà validée en repartant
//    de ses coins, à partir de son image source,
//  - `badge` sur la caméra (bandeau « Brouillon enregistré »).
// ============================================================

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { haptic } from '@/lib/portal-ux';
import {
  ArrowLeft, Camera, Image as ImageIcon, CircleNotch as Loader2,
  ArrowCounterClockwise as Retry, ArrowsClockwise as RotateIcon, Check,
} from '@phosphor-icons/react';
import {
  loadOpenCv, loadDocModel, detectDocumentQuad,
  isPlausibleQuad, warpDocument,
  type Quad, type DocModelSession,
} from '@/lib/document-scanner';
import { detecterQuadScanic, chargerScanic, chargerUVDoc, mettreAPlat } from '@/lib/document-flatten';

export type { Quad };

// ── Moteur de détection : OpenCV (redressement) + modèle ML (bords) ──
// mlSession en STATE (et pas useRef) : crucial. Le modèle (4,5 Mo) met ~2-3s à
// charger au 1er scan ; en ref, sa disponibilité ne déclenchait PAS de
// re-render, donc l'écran de cadrage restait sans IA et le détourage était à
// côté (bug user 19/06/2026). En state, la détection se relance dès que le
// modèle est prêt.
export function useScannerEngine() {
  const [mlSession, setMlSession] = useState<DocModelSession | null>(null);
  const [mlLoading, setMlLoading] = useState(true);
  const [cvState, setCvState] = useState<'loading' | 'ok' | 'failed'>('loading');

  useEffect(() => {
    let cancelled = false;
    // OpenCV : nécessaire au redressement perspective (warpDocument).
    (async () => {
      try {
        await loadOpenCv();
        if (!cancelled) setCvState('ok');
      } catch {
        if (!cancelled) setCvState('failed');
      }
    })();
    // Détecteur de documents et modèle de mise à plat : préchargés en fond dès
    // l'ouverture du scanner, pour que la validation ne les attende pas.
    void chargerScanic().catch(() => { /* repli sur le modèle de saillance */ });
    void chargerUVDoc().catch(() => { /* repli sur le redressement perspective */ });
    // Modèle ML (U²-Net). S'il ne charge pas : rectangle centré + réglage manuel.
    (async () => {
      try {
        const session = await loadDocModel();
        if (!cancelled) setMlSession(session);
      } catch { /* modèle indisponible : centré + manuel */ }
      finally {
        if (!cancelled) setMlLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return { mlSession, mlLoading, cvOk: cvState === 'ok' };
}

// ── Caméra : flux + capture pleine résolution ──────────────────
export function useCameraCapture(active: boolean) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [camError, setCamError] = useState<string | null>(null);
  const [capturing, setCapturing] = useState(false);

  const stopCamera = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  }, []);

  const startCamera = useCallback(async () => {
    setCamError(null);
    try {
      // On demande la plus haute résolution possible (netteté) ET la mise au
      // point continue. Pas de contrainte d'aspect : on laisse le capteur natif.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: 'environment' },
          width: { ideal: 4096 },
          height: { ideal: 4096 },
          // @ts-expect-error focusMode non typé dans MediaTrackConstraints
          focusMode: 'continuous',
        },
        audio: false,
      });
      streamRef.current = stream;
      const v = videoRef.current;
      if (v) {
        v.srcObject = stream;
        await v.play().catch(() => {});
      }
    } catch {
      setCamError("Accès caméra refusé. Autorise la caméra, ou importe une photo depuis ta galerie.");
    }
  }, []);

  useEffect(() => {
    if (active) void startCamera();
    else stopCamera();
    return () => stopCamera();
  }, [active, startCamera, stopCamera]);

  // Capture une image NETTE et pleine résolution.
  //  1) ImageCapture.takePhoto() = pleine résolution capteur (la plus nette,
  //     bien supérieure au flux vidéo de preview) quand le navigateur la gère.
  //  2) Repli : frame vidéo courante en pleine résolution, JPEG qualité 0.95.
  const grabFrame = useCallback(async (): Promise<HTMLImageElement | null> => {
    const stream = streamRef.current;
    try {
      const track = stream?.getVideoTracks?.()[0];
      const ImageCaptureCtor = (window as unknown as { ImageCapture?: new (t: MediaStreamTrack) => {
        takePhoto: (settings?: { imageWidth?: number; imageHeight?: number }) => Promise<Blob>;
        getPhotoCapabilities?: () => Promise<{ imageWidth?: { max?: number }; imageHeight?: { max?: number } }>;
      } }).ImageCapture;
      if (track && typeof ImageCaptureCtor === 'function') {
        const ic = new ImageCaptureCtor(track);
        // On force la PLEINE résolution capteur (souvent 4000x3000) : sinon
        // takePhoto() peut retomber sur la def du flux preview (720p) -> ticket
        // illisible au recadrage (retour user 16/06/2026).
        let settings: { imageWidth?: number; imageHeight?: number } | undefined;
        try {
          const caps = await ic.getPhotoCapabilities?.();
          if (caps?.imageWidth?.max) settings = { imageWidth: caps.imageWidth.max, imageHeight: caps.imageHeight?.max };
        } catch { /* capacités non exposées : takePhoto par défaut */ }
        const blob = await ic.takePhoto(settings).catch(() => ic.takePhoto().catch(() => null));
        if (blob) {
          const url = URL.createObjectURL(blob);
          const img = await loadImage(url);
          URL.revokeObjectURL(url);
          if (img && img.naturalWidth > 0) return img;
        }
      }
    } catch { /* repli ci-dessous */ }

    const v = videoRef.current;
    if (!v || !v.videoWidth) return null;
    const c = document.createElement('canvas');
    c.width = v.videoWidth;
    c.height = v.videoHeight;
    c.getContext('2d')!.drawImage(v, 0, 0);
    return loadImage(c.toDataURL('image/jpeg', 0.95));
  }, []);

  return { videoRef, camError, capturing, setCapturing, startCamera, stopCamera, grabFrame };
}

// ── Étape caméra ───────────────────────────────────────────────

export function CameraStage({
  videoRef, camError, capturing, onShutter, onGalleryFile, onRetryCamera, onBack,
  pagesCount, onSeePages, badge, hint,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  camError: string | null;
  capturing: boolean;
  onShutter: () => void;
  onGalleryFile: (f: File) => void;
  onRetryCamera: () => void;
  onBack: () => void;
  pagesCount: number;
  onSeePages: () => void;
  /** Bandeau flottant en haut (ex : « Brouillon enregistré »). */
  badge?: ReactNode;
  hint?: string;
}) {
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Mise au point au toucher : sur l'appareil photo natif, on appuie sur le
  // document pour le rendre net. Sans ça on perd cette habitude et on scanne
  // flou (retour user 09/09/2026). Toutes les caméras ne l'exposent pas : on
  // l'utilise si elle est disponible, et on ne casse rien sinon.
  const [pointFocus, setPointFocus] = useState<{ x: number; y: number } | null>(null);
  const focaliser = useCallback(async (e: React.PointerEvent<HTMLDivElement>) => {
    const v = videoRef.current;
    const flux = v?.srcObject as MediaStream | null;
    const piste = flux?.getVideoTracks?.()[0];
    if (!v || !piste) return;
    const r = v.getBoundingClientRect();
    const x = Math.min(1, Math.max(0, (e.clientX - r.left) / Math.max(1, r.width)));
    const y = Math.min(1, Math.max(0, (e.clientY - r.top) / Math.max(1, r.height)));
    setPointFocus({ x: e.clientX - r.left, y: e.clientY - r.top });
    window.setTimeout(() => setPointFocus(null), 900);
    try {
      const caps = (piste.getCapabilities?.() ?? {}) as Record<string, unknown>;
      const modes = (caps.focusMode as string[] | undefined) ?? [];
      const avance: Record<string, unknown> = {};
      if ('pointsOfInterest' in caps) avance.pointsOfInterest = [{ x, y }];
      if (modes.includes('single-shot')) avance.focusMode = 'single-shot';
      else if (modes.includes('continuous')) avance.focusMode = 'continuous';
      if (Object.keys(avance).length === 0) return;
      await piste.applyConstraints({ advanced: [avance] } as MediaTrackConstraints);
      haptic('tap');
    } catch { /* caméra sans réglage de mise au point : on n'insiste pas */ }
  }, [videoRef]);
  return (
    // Plein écran. La vidéo est en object-CONTAIN : on voit TOUT le champ de la
    // caméra (aucun zoom/recadrage forcé), le salarié peut zoomer lui-même via
    // sa caméra. Guide statique pour l'aider à bien cadrer et rester à plat.
    <div className="fixed inset-0 z-[100] bg-black">
      {camError ? (
        <div className="flex h-full flex-col items-center justify-center gap-4 px-8 text-center">
          <Camera size={40} className="text-[#5A5F6A]" />
          <p className="text-[13px] leading-relaxed text-[#8A8F98]">{camError}</p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onRetryCamera}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#2A2D35] bg-[#15171A] px-4 text-[13px] font-semibold text-[#E8E9EB] active:scale-95"
            >
              <Retry size={15} /> Réessayer
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              className="inline-flex min-h-[44px] items-center gap-2 rounded-xl border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 px-4 text-[13px] font-semibold text-[#C4B5FD] active:scale-95"
            >
              <ImageIcon size={15} /> Galerie
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
          <video ref={videoRef} playsInline muted autoPlay className="absolute inset-0 h-full w-full object-contain" />
          {/* Couche de mise au point : elle ne capte que le toucher, les
              contrôles flottants restent au-dessus. */}
          <div className="absolute inset-0" onPointerDown={focaliser} aria-hidden />
          {pointFocus && (
            <span
              className="pointer-events-none absolute h-16 w-16 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-[#A78BFA]"
              style={{ left: pointFocus.x, top: pointFocus.y, animation: 'ping 0.9s ease-out 1' }}
            />
          )}
          {/* Guide de cadrage statique (cadre + coins) : aide visuelle sans
              traitement live. Le détourage précis se fait APRÈS la photo. */}
          <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center">
            <div className="relative h-[64%] w-[82%] rounded-xl border-2 border-white/30">
              <span className="absolute -left-0.5 -top-0.5 h-6 w-6 rounded-tl-xl border-l-[3px] border-t-[3px] border-[#A78BFA]" />
              <span className="absolute -right-0.5 -top-0.5 h-6 w-6 rounded-tr-xl border-r-[3px] border-t-[3px] border-[#A78BFA]" />
              <span className="absolute -bottom-0.5 -left-0.5 h-6 w-6 rounded-bl-xl border-b-[3px] border-l-[3px] border-[#A78BFA]" />
              <span className="absolute -bottom-0.5 -right-0.5 h-6 w-6 rounded-br-xl border-b-[3px] border-r-[3px] border-[#A78BFA]" />
            </div>
          </div>
          {badge && (
            <div
              className="absolute left-1/2 z-10 -translate-x-1/2"
              style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
            >
              {badge}
            </div>
          )}
          <div
            className="absolute left-1/2 -translate-x-1/2 rounded-full bg-black/55 px-3.5 py-1.5 text-center text-[11.5px] font-medium text-white backdrop-blur"
            style={{ top: `calc(env(safe-area-inset-top, 0px) + ${badge ? 62 : 60}px)` }}
          >
            {hint ?? 'Posez le document à plat, bien éclairé, dans le cadre'}
          </div>
          {capturing && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <Loader2 size={28} className="animate-spin text-white" />
            </div>
          )}
        </>
      )}

      {/* Bouton retour flottant */}
      <button
        type="button"
        onClick={onBack}
        aria-label="Retour"
        className="absolute left-4 z-10 inline-flex h-11 w-11 items-center justify-center rounded-full bg-black/55 text-white backdrop-blur active:scale-95"
        style={{ top: 'calc(env(safe-area-inset-top, 0px) + 14px)' }}
      >
        <ArrowLeft size={18} />
      </button>

      {/* Contrôles flottants en bas */}
      <div
        className="absolute inset-x-0 bottom-0 flex items-center justify-around px-8"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 28px)', paddingTop: 28, background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)' }}
      >
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur active:scale-95"
          aria-label="Importer depuis la galerie"
        >
          <ImageIcon size={20} />
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onGalleryFile(f); e.target.value = ''; }}
        />
        <button
          type="button"
          onClick={onShutter}
          disabled={!!camError || capturing}
          aria-label="Capturer"
          className="relative inline-flex h-[74px] w-[74px] items-center justify-center rounded-full border-4 border-white/90 bg-white/10 transition-transform active:scale-90 disabled:opacity-40"
        >
          <span className="h-14 w-14 rounded-full bg-white" />
        </button>
        {/* Accès aux pages déjà capturées (ou espace réservé pour garder le centrage) */}
        {pagesCount > 0 ? (
          <button
            type="button"
            onClick={onSeePages}
            aria-label="Voir les pages capturées"
            className="relative inline-flex h-12 w-12 items-center justify-center rounded-full bg-black/45 text-white backdrop-blur active:scale-95"
          >
            <ImageIcon size={20} />
            <span className="absolute -right-1 -top-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#8B5CF6] px-1 text-[10px] font-bold text-white">
              {pagesCount}
            </span>
          </button>
        ) : (
          <div className="h-12 w-12" aria-hidden />
        )}
      </div>
    </div>
  );
}

// ── Étape recadrage (poignées + loupe + rotation + redressement) ──

export function CropStage({
  image, initialQuad, mlSession, mlLoading, cvOk, cancelLabel, onCancel, onValidated,
}: {
  image: HTMLImageElement;
  /** Coins déjà validés pour cette page (recadrage d'une page existante). */
  initialQuad?: Quad | null;
  mlSession: DocModelSession | null;
  mlLoading: boolean;
  cvOk: boolean;
  cancelLabel?: string;
  onCancel: () => void;
  /** `source` = image réellement recadrée (tient compte des rotations). */
  onValidated: (pageDataUri: string, quad: Quad, source: HTMLImageElement, avantBlanchiment?: string) => void;
}) {
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const baseRef = useRef<HTMLCanvasElement | null>(null);
  const overlayRef = useRef<HTMLCanvasElement | null>(null);
  // Image réellement travaillée : l'originale, ou sa version pivotée.
  const [workImage, setWorkImage] = useState<HTMLImageElement>(image);
  const [rotating, setRotating] = useState(false);
  // Quad en coordonnées IMAGE NATURELLE.
  const [quad, setQuad] = useState<Quad | null>(null);
  const [layout, setLayout] = useState<{ scale: number; w: number; h: number }>({ scale: 1, w: 0, h: 0 });
  const [active, setActive] = useState<keyof Quad | null>(null);
  const [loupe, setLoupe] = useState<{ x: number; y: number; sx: number; sy: number } | null>(null);
  const [processing, setProcessing] = useState(false);

  // L'utilisateur a-t-il déplacé un coin à la main ? Si oui, on ne réécrase pas
  // son réglage quand la détection ML arrive (le modèle peut finir de charger
  // APRÈS l'ouverture du cadrage au 1er scan).
  const adjustedRef = useRef(false);
  // Détection en cours : le seul chargement du modèle ne suffisait pas à
  // prévenir l'utilisateur. Les coins sautaient d'un coup sur un écran qui
  // n'affichait rien (retour user 09/09/2026).
  const [detecting, setDetecting] = useState(true);

  // Nouvelle photo : on repart de l'image d'origine, sans rotation.
  useEffect(() => { setWorkImage(image); }, [image]);

  // Effet 1 : image : dessine le fond, calcule l'échelle, pose le rectangle centré
  // (ou les coins déjà validés si on rouvre le cadrage d'une page existante).
  useEffect(() => {
    const wrap = wrapRef.current;
    const base = baseRef.current;
    if (!wrap || !base) return;
    const img = workImage;
    const maxW = wrap.clientWidth;
    const maxH = wrap.clientHeight;
    const scale = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    // Rendu NET sur écran retina : backing store à w*dpr (taille CSS = w/h).
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    base.width = Math.round(w * dpr); base.height = Math.round(h * dpr);
    base.style.width = `${w}px`; base.style.height = `${h}px`;
    const bctx = base.getContext('2d')!;
    bctx.imageSmoothingQuality = 'high';
    bctx.drawImage(img, 0, 0, w * dpr, h * dpr);
    setLayout({ scale, w, h });

    // Recadrage d'une page déjà validée : on repart de SES coins et l'IA ne
    // doit pas les écraser (le salarié a déjà fait son réglage).
    if (initialQuad && img === image) {
      adjustedRef.current = true;
      setQuad(initialQuad);
      return;
    }
    adjustedRef.current = false;
    // Rectangle centré par défaut (en attendant la détection ML).
    const ix = img.naturalWidth * 0.08;
    const iy = img.naturalHeight * 0.08;
    setQuad({
      tl: { x: ix, y: iy },
      tr: { x: img.naturalWidth - ix, y: iy },
      br: { x: img.naturalWidth - ix, y: img.naturalHeight - iy },
      bl: { x: ix, y: img.naturalHeight - iy },
    });
    // `initialQuad` volontairement hors dépendances : il ne change jamais pour
    // une même image, et le relire relancerait le calcul de layout pour rien.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workImage, image]);

  // Effet 2 : détection ML : pose les 4 coins du document dès que le modèle ET
  // OpenCV sont prêts (ils peuvent charger APRÈS l'ouverture du cadrage au 1er
  // scan, d'où le re-run sur mlSession/cvOk). Sans détection plausible, on garde
  // le rectangle centré, rectifiable à la main. On n'écrase jamais un réglage manuel.
  useEffect(() => {
    if (!mlSession || !cvOk) return;
    let cancelled = false;
    setDetecting(true);
    (async () => {
      try {
        // Détecteur de documents d'abord : il répond en une centaine de ms et
        // ne se trompe pas d'objet sur un fond chargé, là où le modèle de
        // saillance détoure le meuble sur lequel le ticket est posé.
        let trouve: Quad | null = null;
        try {
          trouve = await detecterQuadScanic(workImage);
        } catch { /* on retombe sur le modèle de saillance */ }
        if (!trouve || !isPlausibleQuad(trouve, workImage.naturalWidth, workImage.naturalHeight)) {
          trouve = await detectDocumentQuad(mlSession, workImage);
        }
        if (!cancelled && trouve && !adjustedRef.current && isPlausibleQuad(trouve, workImage.naturalWidth, workImage.naturalHeight)) {
          setQuad(trouve);
        }
      } catch { /* on garde le rectangle centré */ }
      finally { if (!cancelled) setDetecting(false); }
    })();
    return () => { cancelled = true; };
  }, [workImage, mlSession, cvOk]);

  // Redessine l'overlay (quad + poignées) à chaque changement.
  useEffect(() => {
    const overlay = overlayRef.current;
    if (!overlay || !quad) return;
    const dpr = Math.min(3, window.devicePixelRatio || 1);
    overlay.width = Math.round(layout.w * dpr);
    overlay.height = Math.round(layout.h * dpr);
    overlay.style.width = `${layout.w}px`;
    overlay.style.height = `${layout.h}px`;
    const ctx = overlay.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0); // dessine en coordonnées CSS, rendu net
    ctx.clearRect(0, 0, layout.w, layout.h);
    const pts = [quad.tl, quad.tr, quad.br, quad.bl].map((p) => ({ x: p.x * layout.scale, y: p.y * layout.scale }));
    ctx.beginPath();
    ctx.moveTo(pts[0].x, pts[0].y);
    pts.slice(1).forEach((p) => ctx.lineTo(p.x, p.y));
    ctx.closePath();
    ctx.fillStyle = 'rgba(139,92,246,0.12)';
    ctx.fill();
    ctx.lineWidth = 2;
    ctx.strokeStyle = '#8B5CF6';
    ctx.stroke();
    pts.forEach((p) => {
      ctx.beginPath();
      ctx.arc(p.x, p.y, 11, 0, Math.PI * 2);
      ctx.fillStyle = '#0B0C0E';
      ctx.fill();
      ctx.lineWidth = 3;
      ctx.strokeStyle = '#A78BFA';
      ctx.stroke();
    });
  }, [quad, layout]);

  const keys: (keyof Quad)[] = ['tl', 'tr', 'br', 'bl'];
  const pointerFromEvent = (e: React.PointerEvent) => {
    const rect = overlayRef.current!.getBoundingClientRect();
    return { dx: e.clientX - rect.left, dy: e.clientY - rect.top };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (!quad) return;
    const { dx, dy } = pointerFromEvent(e);
    // Trouve la poignée la plus proche (< 32px).
    let best: keyof Quad | null = null;
    let bestD = 32;
    for (const k of keys) {
      const px = quad[k].x * layout.scale;
      const py = quad[k].y * layout.scale;
      const d = Math.hypot(px - dx, py - dy);
      if (d < bestD) { bestD = d; best = k; }
    }
    if (best) {
      adjustedRef.current = true; // l'IA ne réécrasera plus le réglage manuel
      setActive(best);
      (e.target as Element).setPointerCapture?.(e.pointerId);
      setLoupe({ x: dx, y: dy, sx: quad[best].x, sy: quad[best].y });
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (!active || !quad) return;
    const { dx, dy } = pointerFromEvent(e);
    const tx = Math.max(0, Math.min(workImage.naturalWidth, dx / layout.scale));
    const ty = Math.max(0, Math.min(workImage.naturalHeight, dy / layout.scale));
    // Lissage : le coin suit le doigt avec un léger amortissement (un poil plus
    // lent, moins nerveux pour le réglage fin : retour user 13/06/2026).
    const cur = quad[active];
    const K = 0.5;
    const nx = cur.x + (tx - cur.x) * K;
    const ny = cur.y + (ty - cur.y) * K;
    setQuad({ ...quad, [active]: { x: nx, y: ny } });
    setLoupe({ x: dx, y: dy, sx: nx, sy: ny });
  };

  const onPointerUp = () => { setActive(null); setLoupe(null); };

  // Pivoter : on tourne l'image de travail d'un quart de tour. La détection
  // repart de zéro sur l'image pivotée (effets 1 et 2 dépendent de workImage).
  const rotate = async () => {
    if (rotating) return;
    setRotating(true);
    try {
      const rotated = await rotateImage(workImage, 90);
      if (rotated) setWorkImage(rotated);
    } finally {
      setRotating(false);
    }
  };

  // Valide : met le document à plat (modèle de dewarping si disponible, sinon
  // redressement perspective à quatre coins), retire le liseré de fond, blanchit
  // le papier, puis compresse en JPEG.
  const validate = async () => {
    if (!quad) return;
    setProcessing(true);
    try {
      // Repli commun : perspective à quatre coins, ou simple recadrage.
      const parPerspective = (): HTMLCanvasElement | null => {
        if (cvOk) {
          const w = warpDocument(workImage, quad);
          if (w) return w;
        }
        const minX = Math.min(quad.tl.x, quad.bl.x);
        const maxX = Math.max(quad.tr.x, quad.br.x);
        const minY = Math.min(quad.tl.y, quad.tr.y);
        const maxY = Math.max(quad.bl.y, quad.br.y);
        const cw = Math.max(1, maxX - minX);
        const ch = Math.max(1, maxY - minY);
        const c = document.createElement('canvas');
        c.width = cw; c.height = ch;
        c.getContext('2d')!.drawImage(workImage, minX, minY, cw, ch, 0, 0, cw, ch);
        return c;
      };

      let out: HTMLCanvasElement | null = null;
      let brut: HTMLCanvasElement | null = null;
      try {
        const r = await mettreAPlat(workImage, quad, parPerspective);
        if (r) { out = r.canvas; brut = r.source; }
      } catch { /* traitement indisponible : on garde le redressement simple */ }
      if (!out) out = parPerspective();
      if (!out) return;
      if (!brut) brut = out;
      // Downscale à 2000px + JPEG 0.85 : le doc est déjà détouré serré (donc très
      // lisible à cette résolution) et GPT-4o Vision redimensionne de toute façon
      // vers ~1536px. Transfert plus léger = envoi plus rapide, sans perte utile.
      const finalCanvas = downscale(out, 2000);
      const dataUri = finalCanvas.toDataURL('image/jpeg', 0.85);
      // Version non blanchie transmise en plus : la retouche a besoin d'une
      // image dont les niveaux ne sont pas saturés pour mesurer l'inclinaison.
      const avantBlanchiment = downscale(brut, 2000).toDataURL('image/jpeg', 0.88);
      onValidated(dataUri, quad, workImage, avantBlanchiment);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div ref={wrapRef} className="relative flex min-h-0 flex-1 items-center justify-center overflow-hidden bg-black p-3">
        <div className="relative" style={{ width: layout.w, height: layout.h }}>
          <canvas ref={baseRef} className="block" />
          <canvas
            ref={overlayRef}
            className="absolute inset-0 touch-none"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
          {/* Loupe épinglée au coin OPPOSÉ au coin déplacé : ainsi elle n'est
              jamais sous le doigt (retour user 13/06/2026). */}
          {loupe && active && (
            <Loupe image={workImage} loupe={loupe} corner={active} />
          )}
          {/* Détection ML en cours (modèle pas encore chargé) : on prévient pour
              que l'utilisateur attende le placement auto des coins. */}
          {(detecting || mlLoading) && (
            <div className="pointer-events-none absolute left-1/2 top-3 flex -translate-x-1/2 items-center gap-2 rounded-full bg-black/65 px-3 py-1.5 text-[11.5px] font-medium text-white backdrop-blur">
              <Loader2 size={13} className="animate-spin" /> Recherche des bords du document…
            </div>
          )}
          {!detecting && !mlLoading && (
            <div className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/65 px-3 py-1.5 text-[11.5px] font-medium text-white backdrop-blur">
              Ajuste les coins si besoin
            </div>
          )}
        </div>
      </div>

      <div
        className="flex flex-shrink-0 items-stretch gap-2.5 border-t border-[#1F2127] bg-[#0B0C0E] px-4 py-4"
        style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)' }}
      >
        <button
          type="button"
          onClick={onCancel}
          className="inline-flex min-h-[52px] min-w-[84px] flex-col items-center justify-center gap-1 rounded-xl border border-[#2A2D35] bg-[#15171A] px-3 text-[11px] font-semibold text-[#E8E9EB] active:scale-95"
        >
          <Retry size={17} /> {cancelLabel ?? 'Reprendre'}
        </button>
        <button
          type="button"
          onClick={rotate}
          disabled={rotating}
          className="inline-flex min-h-[52px] min-w-[76px] flex-col items-center justify-center gap-1 rounded-xl border border-[#2A2D35] bg-[#15171A] px-3 text-[11px] font-semibold text-[#E8E9EB] active:scale-95 disabled:opacity-60"
        >
          {rotating ? <Loader2 size={17} className="animate-spin" /> : <RotateIcon size={17} />} Pivoter
        </button>
        <button
          type="button"
          onClick={validate}
          // Pendant la recherche des bords, valider fixerait le rectangle par
          // défaut : on attend que les coins soient posés.
          disabled={processing || detecting}
          className="inline-flex min-h-[52px] flex-1 items-center justify-center gap-2 rounded-xl px-4 text-[14px] font-semibold text-white transition-all active:scale-[0.98] disabled:opacity-60"
          style={{ background: 'linear-gradient(180deg, #8B5CF6, #7C3AED)', boxShadow: '0 10px 24px -8px rgba(139,92,246,0.45)' }}
        >
          {processing || detecting ? <Loader2 size={18} className="animate-spin" /> : <Check size={18} />}
          {processing ? 'Traitement...' : detecting ? 'Recherche...' : 'Valider'}
        </button>
      </div>
    </div>
  );
}

// Loupe circulaire grossissante, ÉPINGLÉE dans le coin opposé au coin déplacé
// (jamais sous le doigt). `loupe.sx/sy` = point source (coords image naturelle).
function Loupe({ image, loupe, corner }: { image: HTMLImageElement; loupe: { sx: number; sy: number }; corner: keyof Quad }) {
  const ref = useRef<HTMLCanvasElement | null>(null);
  const SIZE = 110;
  const ZOOM = 2.6;
  useEffect(() => {
    const c = ref.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.save();
    ctx.beginPath();
    ctx.arc(SIZE / 2, SIZE / 2, SIZE / 2, 0, Math.PI * 2);
    ctx.clip();
    ctx.fillStyle = '#0B0C0E';
    ctx.fillRect(0, 0, SIZE, SIZE);
    const src = SIZE / ZOOM;
    ctx.drawImage(image, loupe.sx - src / 2, loupe.sy - src / 2, src, src, 0, 0, SIZE, SIZE);
    // Réticule
    ctx.strokeStyle = 'rgba(167,139,250,0.95)';
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(SIZE / 2 - 11, SIZE / 2); ctx.lineTo(SIZE / 2 + 11, SIZE / 2); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(SIZE / 2, SIZE / 2 - 11); ctx.lineTo(SIZE / 2, SIZE / 2 + 11); ctx.stroke();
    ctx.restore();
  }, [image, loupe]);

  // Coin opposé : tl→bas-droite, tr→bas-gauche, br→haut-gauche, bl→haut-droite.
  const pos: React.CSSProperties =
    corner === 'tl' ? { right: 10, bottom: 10 }
    : corner === 'tr' ? { left: 10, bottom: 10 }
    : corner === 'br' ? { left: 10, top: 10 }
    : { right: 10, top: 10 };
  return (
    <canvas
      ref={ref}
      width={SIZE}
      height={SIZE}
      className="pointer-events-none absolute z-10 rounded-full border-2 border-[#A78BFA] shadow-[0_8px_24px_rgba(0,0,0,0.55)]"
      style={pos}
    />
  );
}

// ── Helpers image ──────────────────────────────────────────────

export function downscale(src: HTMLCanvasElement, maxW: number): HTMLCanvasElement {
  if (src.width <= maxW) return src;
  const scale = maxW / src.width;
  const out = document.createElement('canvas');
  out.width = maxW;
  out.height = Math.round(src.height * scale);
  out.getContext('2d')!.drawImage(src, 0, 0, out.width, out.height);
  return out;
}

/// <summary>Charge une URL (dataURI/blobURL) en HTMLImageElement.</summary>
export function loadImage(src: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = src;
  });
}

/// <summary>Pivote une image d'un quart de tour (sens horaire) sans perte visible.</summary>
export function rotateImage(img: HTMLImageElement, deg: 90 | 180 | 270): Promise<HTMLImageElement | null> {
  // Demi-tour : les dimensions ne bougent pas. Quart de tour : elles s'échangent.
  const demiTour = deg === 180;
  const c = document.createElement('canvas');
  c.width = demiTour ? img.naturalWidth : img.naturalHeight;
  c.height = demiTour ? img.naturalHeight : img.naturalWidth;
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.translate(c.width / 2, c.height / 2);
  ctx.rotate((deg * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return loadImage(c.toDataURL('image/jpeg', 0.92));
}

/// <summary>Pivote une image encodée en dataURI (vignette d'une page validée).</summary>
export async function rotateDataUri(dataUri: string, deg: 90 | 180 | 270 = 90): Promise<string | null> {
  const img = await loadImage(dataUri);
  if (!img) return null;
  const rotated = await rotateImage(img, deg);
  return rotated?.src ?? null;
}

/// <summary>Ré-encode une image DÉJÀ chargée à une largeur et une qualité
/// données. Sert à fabriquer les copies légères rangées dans le brouillon.
/// On travaille sur l'élément (et pas sur son URL) : une photo prise via
/// ImageCapture vient d'un blob URL déjà révoqué, seul l'élément reste lisible.</summary>
export function compressImageElement(img: HTMLImageElement, maxW: number, quality: number): string {
  const c = document.createElement('canvas');
  const scale = img.naturalWidth > maxW ? maxW / img.naturalWidth : 1;
  c.width = Math.max(1, Math.round(img.naturalWidth * scale));
  c.height = Math.max(1, Math.round(img.naturalHeight * scale));
  const ctx = c.getContext('2d')!;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', quality);
}

/// <summary>Même chose à partir d'une image encodée (dataURI).</summary>
export async function compressDataUri(dataUri: string, maxW: number, quality: number): Promise<string> {
  const img = await loadImage(dataUri);
  if (!img) return dataUri;
  return compressImageElement(img, maxW, quality);
}
